import type { Session } from './types';

/**
 * Nazwy ról Keycloaka sterujących dostępem — dopasowywane bez rozróżniania
 * wielkości liter (realm bywa niekonsekwentny w konwencji nazewnictwa ról,
 * a literówka w wielkości liter nie powinna po cichu odciąć dostępu).
 */
const ISKK_USER_ROLE = 'iskk_user';
const ISKK_ADMIN_ROLE = 'iskk_admin';

interface OidcLikeUser {
  profile?: unknown;
  access_token?: string;
}

/**
 * Realm roles Keycloaka (`realm_access.roles`) — czytane z DWÓCH miejsc i
 * sumowane, bo nie wiadomo z góry, które akurat ma ten realm skonfigurowane:
 *  – `profile` (ID token, już zdekodowany przez oidc-client-ts) — trafiają
 *    tu tylko, jeśli mapper „User Realm Role” w scope `roles` ma włączone
 *    „Add to ID token” (domyślnie włączone w standardowym Keycloaku),
 *  – surowy `access_token` (JWT), zdekodowany ręcznie tutaj — token
 *    dostępowy Keycloaka niemal zawsze niesie `realm_access.roles`,
 *    niezależnie od konfiguracji mapperów ID tokenu, więc to pewniejsze
 *    źródło.
 *
 * WYŁĄCZNIE do sterowania interfejsem (jak cały `AccessScope` — patrz
 * CLAUDE.md, sekcja „Model autoryzacji”) — to nie jest weryfikacja podpisu
 * tokenu, więc nie może pełnić roli zabezpieczenia. Prawdziwą autoryzację
 * `/api/admin/*` i reszty endpointów musi wymuszać backend na podstawie
 * tego samego tokenu.
 */
export function extractRealmRoles(user: OidcLikeUser | null | undefined): string[] {
  if (!user) return [];
  const fromProfile = readRealmAccessRoles(user.profile);
  const fromAccessToken = user.access_token
    ? readRealmAccessRoles(decodeJwtPayload(user.access_token))
    : [];
  return Array.from(new Set([...fromProfile, ...fromAccessToken]));
}

function readRealmAccessRoles(value: unknown): string[] {
  if (!value || typeof value !== 'object') return [];
  const realmAccess = (value as Record<string, unknown>).realm_access;
  if (!realmAccess || typeof realmAccess !== 'object') return [];
  const roles = (realmAccess as Record<string, unknown>).roles;
  return Array.isArray(roles)
    ? roles.filter((role): role is string => typeof role === 'string')
    : [];
}

/** Dekoduje payload JWT bez weryfikacji podpisu — wyłącznie do odczytu claimów w UI. */
function decodeJwtPayload(token: string): unknown {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const percentEncoded = atob(normalized)
      .split('')
      .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
      .join('');
    return JSON.parse(decodeURIComponent(percentEncoded)) as unknown;
  } catch {
    return null;
  }
}

function hasRole(roles: string[], role: string): boolean {
  return roles.some((candidate) => candidate.toLowerCase() === role);
}

export function isIskkAdmin(session: Session | null): boolean {
  return session?.method === 'oidc' && hasRole(session.roles, ISKK_ADMIN_ROLE);
}

/**
 * Szczegółowe dane statystyczne (mapa, wykresy diecezjalne, wskaźniki) —
 * widoczne dla roli `iskk_user`/`iskk_admin` z eKurii, oraz dla KAŻDEGO
 * zalogowanego tokenem (logowanie tokenem samo w sobie jest już nadaniem
 * dostępu do konkretnego zakresu danych, nie ma tam osobnego pojęcia roli).
 */
export function hasDetailedAccess(session: Session | null): boolean {
  if (!session) return false;
  if (session.method === 'token') return true;
  return isIskkAdmin(session) || hasRole(session.roles, ISKK_USER_ROLE);
}

/**
 * Sesja tokenem ze scope `diocese` — steruje widokami „moja diecezja/moje
 * dekanaty/moje parafie” na `/moje-dane`. Dziś to jedyna ścieżka, którą
 * backend faktycznie scope'uje po diecezji (patrz CLAUDE.md, sekcja
 * Backend — OIDC nie niesie dziś żadnej diecezji, więc zawsze ląduje jako
 * `national`, nigdy `diocese`).
 */
export function isDioceseScoped(session: Session | null): boolean {
  return session?.method === 'token' && session.scope.kind === 'diocese';
}

/** Sesja tokenem ze scope `parish` — steruje widokiem „moja parafia” na `/moje-dane`. */
export function isParishScoped(session: Session | null): boolean {
  return session?.method === 'token' && session.scope.kind === 'parish';
}
